import { compareDisplays } from "./displayLaunchModel";
import { matchDateLabel, matchPhase } from "./matchDiscovery";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import {
  listBullseyeMatchesPage,
  listMatchesPage,
  listMyTournamentParticipants,
  listOwnedBullseyeMatches,
  listOwnedMatches,
  listTournamentsPage,
} from "../../lib/leaderboardApi";
import { buildAuthReturnPath, buildCurrentReturnTo } from "../../lib/authReturn";
import { CompetitionAuthGate } from "../Competition/CompetitionRoutePanels";
import { formatCalendarDate } from "../../utils/date";
import "../Leaderboard/Leaderboard.css";
import "./PortalSurfaces.css";

const DISPLAY_TYPE_FILTERS = ["all", "matches", "tournaments"];
const DISPLAY_STATUS_FILTERS = ["all", "scheduled", "in_progress", "active", "draft", "completed"];

function formatDateTime(value) {
  if (!value) return "Date not set";
  return formatCalendarDate(value, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateRange(item) {
  const start = item?.startDate || item?.matchDate;
  const end = item?.endDate;
  if (!start) return "Date not set";
  const startLabel = formatDateTime(start);
  if (!end) return startLabel;
  return `${startLabel} - ${formatDateTime(end)}`;
}

function formatStatus(value) {
  const label = String(value || "scheduled").replace(/_/g, " ");
  return label.replace(/^./, (char) => char.toUpperCase());
}

function normalizeDisplayTypeFilter(value) {
  return DISPLAY_TYPE_FILTERS.includes(value) ? value : "all";
}

function normalizeDisplayStatusFilter(value) {
  return DISPLAY_STATUS_FILTERS.includes(value) ? value : "all";
}

function buildLocationLabel(match) {
  const primary = String(match?.location || "").trim();
  if (primary) return primary;
  const city = String(match?.city || "").trim();
  const state = String(match?.state || "").trim();
  const range = String(match?.rangeName || "").trim();
  if (city && state) return `${city}, ${state}`;
  if (range && state) return `${range}, ${state}`;
  if (range) return range;
  if (state) return state;
  return "Location not set";
}

function tournamentFamilyLabel(value) {
  if (value === "arc") return "NRA ARC";
  if (value === "bullseye") return "Bullseye";
  if (value === "uspsaCompetition") return "USPSA";
  if (value === "uspsaSteelChallenge") return "Steel Challenge";
  return value || "Tournament";
}

function normalizeMatchDisplay(match, family = "arc") {
  const matchId = String(match?.matchId || match?.id || "").trim();
  const sortValue = itemTimeValue(match);
  const isBullseye = family === "bullseye";
  return {
    id: matchId,
    type: "match",
    title: match?.matchName || "Untitled Match",
    status: matchPhase(match),
    upcoming: matchPhase(match) === "scheduled",
    visibility: String(match?.publicationState || "published_private"),
    dateLabel: matchDateLabel(match),
    locationLabel: buildLocationLabel(match),
    familyLabel: isBullseye ? "Bullseye" : "NRA ARC",
    sourceLabel: "Match",
    sortValue,
    displayPath: `/matches/${isBullseye ? "bullseye" : "arc"}/${encodeURIComponent(matchId)}/display`,
    workbenchPath: `/matches/manage/${isBullseye ? "bullseye" : "arc"}/${encodeURIComponent(matchId)}`,
    publicPath: `/matches/${isBullseye ? "bullseye" : "arc"}/${encodeURIComponent(matchId)}`,
  };
}

function normalizeTournamentDisplay(tournament, sourceLabel = "Owner") {
  const tournamentId = String(tournament?.tournamentId || "").trim();
  const sortValue = itemTimeValue(tournament);
  return {
    id: tournamentId,
    type: "tournament",
    title:
      tournament?.name ||
      tournament?.tournamentName ||
      tournament?.tournamentDisplayName ||
      "Untitled Tournament",
    status: String(tournament?.status || "active"),
    visibility: String(tournament?.visibility || "public"),
    dateLabel: formatDateRange(tournament),
    familyLabel: tournamentFamilyLabel(tournament?.competitionFamily || tournament?.tournamentFamily),
    sourceLabel,
    sortValue,
    displayPath: `/tournaments/${encodeURIComponent(tournamentId)}/leaderboard`,
    workbenchPath: `/matches/manage/tournaments/${encodeURIComponent(tournamentId)}`,
    publicPath: `/tournaments/${encodeURIComponent(tournamentId)}`,
  };
}

function itemTimeValue(item) {
  const value = new Date(item?.startDate || item?.matchDate || item?.createdAt || 0).getTime();
  return Number.isNaN(value) ? Number.POSITIVE_INFINITY : value;
}

function displayAllRowsPath(path) {
  const [pathname, query = ""] = String(path || "").split("?");
  const params = new URLSearchParams(query);
  params.set("rows", "all");
  const nextQuery = params.toString();
  return `${pathname}${nextQuery ? `?${nextQuery}` : ""}`;
}

function displayMatchesSearch(item, query) {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  if (!normalizedQuery) return true;

  return [
    item?.title,
    item?.familyLabel,
    item?.sourceLabel,
    item?.locationLabel,
    item?.status,
    item?.visibility,
  ].some((value) => String(value || "").toLowerCase().includes(normalizedQuery));
}

async function loadOwnedTournamentPages() {
  const tournaments = [];
  let nextToken = null;
  let pageCount = 0;

  do {
    const page = await listTournamentsPage({
      owned: true,
      visibility: null,
      status: "all",
      limit: 50,
      sortDir: "asc",
      nextToken,
    });
    tournaments.push(...(Array.isArray(page?.data) ? page.data : []));
    nextToken = page?.nextToken || null;
    pageCount += 1;
  } while (nextToken && pageCount < 10);

  return {
    data: tournaments,
    partial: Boolean(nextToken),
  };
}

function DirectorDisplaysPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearchQuery = String(searchParams.get("q") || "").trim();
  const initialTypeFilter = normalizeDisplayTypeFilter(searchParams.get("type") || "all");
  const initialStatusFilter = normalizeDisplayStatusFilter(searchParams.get("status") || "all");
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [matchDisplays, setMatchDisplays] = useState([]);
  const [tournamentDisplays, setTournamentDisplays] = useState([]);
  const [loadErrors, setLoadErrors] = useState([]);
  const [searchInput, setSearchInput] = useState(initialSearchQuery);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [typeFilter, setTypeFilter] = useState(initialTypeFilter);
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);

  useEffect(() => {
    const nextSearchQuery = String(searchParams.get("q") || "").trim();
    const nextTypeFilter = normalizeDisplayTypeFilter(searchParams.get("type") || "all");
    const nextStatusFilter = normalizeDisplayStatusFilter(searchParams.get("status") || "all");
    setSearchInput(nextSearchQuery);
    setSearchQuery(nextSearchQuery);
    setTypeFilter(nextTypeFilter);
    setStatusFilter(nextStatusFilter);
  }, [searchParams]);

  useEffect(() => {
    let active = true;

    async function loadDisplays() {
      setLoading(true);
      setLoadErrors([]);
      try {
        const user = await getCurrentUser();
        const attrs = await fetchUserAttributes();
        const userId = String(attrs?.sub || user?.userId || "").trim();
        if (!active) return;

        setSignedIn(Boolean(userId));
        if (!userId) {
          setMatchDisplays([]);
          setTournamentDisplays([]);
          return;
        }

        const [ownedArcResult, publicArcResult, ownedBullseyeResult, publicBullseyeResult, ownedTournamentResult, organizerResult] = await Promise.allSettled([
          listOwnedMatches(userId, { limit: 100, sortDir: "asc" }),
          listMatchesPage({ limit: 100, status: null, publicationState: "published_public", sortDir: "asc" }),
          listOwnedBullseyeMatches(userId, { limit: 100, sortDir: "asc" }),
          listBullseyeMatchesPage({ limit: 100, status: null, publicationState: "published_public", sortDir: "asc" }),
          loadOwnedTournamentPages(),
          listMyTournamentParticipants({ status: "approved" }),
        ]);
        if (!active) return;

        const nextErrors = [];
        const ownedArcRows = ownedArcResult.status === "fulfilled" ? ownedArcResult.value?.data || [] : [];
        const publicArcRows = publicArcResult.status === "fulfilled" ? publicArcResult.value?.data || [] : [];
        const ownedBullseyeRows = ownedBullseyeResult.status === "fulfilled" ? ownedBullseyeResult.value?.data || [] : [];
        const publicBullseyeRows = publicBullseyeResult.status === "fulfilled" ? publicBullseyeResult.value?.data || [] : [];
        const publicArcById = new Map(publicArcRows.map((match) => [String(match?.matchId || ""), match]));
        const publicBullseyeById = new Map(publicBullseyeRows.map((match) => [String(match?.matchId || ""), match]));

        if (ownedArcResult.status !== "fulfilled") {
          nextErrors.push(ownedArcResult.reason?.message || "Owned ARC matches could not be loaded.");
        }
        if (publicArcResult.status !== "fulfilled") {
          nextErrors.push(publicArcResult.reason?.message || "Published ARC matches could not be checked.");
        }
        if (ownedBullseyeResult.status !== "fulfilled") {
          nextErrors.push(ownedBullseyeResult.reason?.message || "Owned Bullseye matches could not be loaded.");
        }
        if (publicBullseyeResult.status !== "fulfilled") {
          nextErrors.push(publicBullseyeResult.reason?.message || "Published Bullseye matches could not be checked.");
        }
        if (ownedTournamentResult.status !== "fulfilled") {
          nextErrors.push(ownedTournamentResult.reason?.message || "Owned tournaments could not be loaded.");
        } else if (ownedTournamentResult.value?.partial) {
          nextErrors.push("Owned tournaments returned more rows than this display launcher shows.");
        }
        if (organizerResult.status !== "fulfilled") {
          nextErrors.push(organizerResult.reason?.message || "Organizer tournament access could not be loaded.");
        }

        const normalizedArcMatches = ownedArcRows
          .map((match) => {
            const matchId = String(match?.matchId || match?.id || "");
            return normalizeMatchDisplay({
              ...match,
              ...(publicArcById.get(matchId) || {}),
            }, "arc");
          })
          .filter((match) => Boolean(match.id));

        const normalizedBullseyeMatches = ownedBullseyeRows
          .map((match) => {
            const matchId = String(match?.matchId || match?.id || "");
            return normalizeMatchDisplay({
              ...match,
              ...(publicBullseyeById.get(matchId) || {}),
            }, "bullseye");
          })
          .filter((match) => Boolean(match.id));

        const normalizedMatches = [...normalizedArcMatches, ...normalizedBullseyeMatches]
          .sort(compareDisplays);

        const ownedTournamentRows = ownedTournamentResult.status === "fulfilled" ? ownedTournamentResult.value?.data || [] : [];
        const ownedTournamentIds = new Set(ownedTournamentRows.map((tournament) => String(tournament?.tournamentId || "")));
        const organizerRows = organizerResult.status === "fulfilled" ? organizerResult.value || [] : [];
        const normalizedTournaments = [
          ...ownedTournamentRows.map((tournament) => normalizeTournamentDisplay(tournament, "Owner")),
          ...organizerRows
            .filter((row) => !ownedTournamentIds.has(String(row?.tournamentId || "")))
            .map((row) => normalizeTournamentDisplay(row, "Organizer")),
        ]
          .filter((tournament) => Boolean(tournament.id))
          .sort(compareDisplays);

        setMatchDisplays(normalizedMatches);
        setTournamentDisplays(normalizedTournaments);
        setLoadErrors(nextErrors);
      } catch (authError) {
        if (!active) return;
        const message = String(authError?.message || "");
        setSignedIn(false);
        setMatchDisplays([]);
        setTournamentDisplays([]);
        setLoadErrors(message && !/not authenticated|sign in|no current user/i.test(message) ? [message] : []);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadDisplays();
    return () => {
      active = false;
    };
  }, []);

  const displayCount = matchDisplays.length + tournamentDisplays.length;
  const filteredMatchDisplays = useMemo(() => {
    if (typeFilter === "tournaments") return [];
    return matchDisplays.filter((item) => {
      const matchesStatus = statusFilter === "all" || item.status === statusFilter;
      return matchesStatus && displayMatchesSearch(item, searchQuery);
    });
  }, [matchDisplays, searchQuery, statusFilter, typeFilter]);
  const filteredTournamentDisplays = useMemo(() => {
    if (typeFilter === "matches") return [];
    return tournamentDisplays.filter((item) => {
      const matchesStatus = statusFilter === "all" || item.status === statusFilter;
      return matchesStatus && displayMatchesSearch(item, searchQuery);
    });
  }, [searchQuery, statusFilter, tournamentDisplays, typeFilter]);
  const filteredDisplayCount = filteredMatchDisplays.length + filteredTournamentDisplays.length;
  const publicCount = useMemo(
    () => [
      ...filteredMatchDisplays.map((item) => item.visibility),
      ...filteredTournamentDisplays.map((item) => item.visibility),
    ].filter((visibility) => String(visibility).toLowerCase().includes("public")).length,
    [filteredMatchDisplays, filteredTournamentDisplays],
  );
  const hasActiveFilters = Boolean(searchQuery) || typeFilter !== "all" || statusFilter !== "all";
  const activeFilterLabels = [
    typeFilter !== "all" ? (typeFilter === "matches" ? "Matches" : "Tournaments") : null,
    statusFilter !== "all" ? formatStatus(statusFilter) : null,
    searchQuery ? `Search: ${searchQuery}` : null,
  ].filter(Boolean);
  const signInPath = buildAuthReturnPath("/auth/sign-in", buildCurrentReturnTo(location, "/matches/manage/displays"));

  function handleSearchSubmit(event) {
    event.preventDefault();
    const nextQuery = searchInput.trim();
    setSearchQuery(nextQuery);
    const params = new URLSearchParams(searchParams);
    if (nextQuery) {
      params.set("q", nextQuery);
    } else {
      params.delete("q");
    }
    setSearchParams(params, { replace: true });
  }

  function handleTypeFilterChange(nextTypeFilter) {
    const normalizedType = normalizeDisplayTypeFilter(nextTypeFilter);
    setTypeFilter(normalizedType);
    const params = new URLSearchParams(searchParams);
    if (normalizedType === "all") {
      params.delete("type");
    } else {
      params.set("type", normalizedType);
    }
    setSearchParams(params, { replace: true });
  }

  function handleStatusFilterChange(nextStatusFilter) {
    const normalizedStatus = normalizeDisplayStatusFilter(nextStatusFilter);
    setStatusFilter(normalizedStatus);
    const params = new URLSearchParams(searchParams);
    if (normalizedStatus === "all") {
      params.delete("status");
    } else {
      params.set("status", normalizedStatus);
    }
    setSearchParams(params, { replace: true });
  }

  function handleClearFilters() {
    setSearchInput("");
    setSearchQuery("");
    setTypeFilter("all");
    setStatusFilter("all");
    setSearchParams({}, { replace: true });
  }

  return (
    <section className="leaderboard-page portal-surface director-displays-page">
      <Helmet>
        <title>Displays | Range Pocket Competition</title>
      </Helmet>

      <div className="match-board-hero">
        <div>
          <h1>Displays</h1>
          <p className="competition-page-lede">
            Open match and tournament leaderboards for monitors, projectors,
            tablets, and range information screens. Live events appear first, followed by upcoming and recent events.
          </p>
        </div>
      </div>

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel">
          <div className="owner-registration-section-label">Loading</div>
          <strong>Loading display launchers</strong>
          <span>Checking owned matches and tournament access</span>
        </div>
      )}

      {!loading && !signedIn && (
        <CompetitionAuthGate
          eyebrow="Director access"
          title="Sign in to open displays"
          signInPath={signInPath}
          links={[
            { to: "/matches/manage", label: "Director Console" },
            { to: "/matches", label: "Public Matches" },
          ]}
        >
          Display launchers use your owned matches and approved tournament access to open match-day fullscreen views.
          {loadErrors.length > 0 && <p className="leaderboard-error-inline">{loadErrors.join(" ")}</p>}
        </CompetitionAuthGate>
      )}

      {!loading && signedIn && (
        <div className="leaderboard-stack">
          <section className="leaderboard-card director-console-summary">
            <div className="director-summary-item">
              <span>Matching displays</span>
              <strong>{filteredDisplayCount}</strong>
            </div>
            <div className="director-summary-item">
              <span>Matches shown</span>
              <strong>{filteredMatchDisplays.length}</strong>
            </div>
            <div className="director-summary-item">
              <span>Tournaments shown</span>
              <strong>{filteredTournamentDisplays.length}</strong>
            </div>
            <div className="director-summary-item">
              <span>Public shown</span>
              <strong>{publicCount}</strong>
            </div>
          </section>

          {displayCount > 0 && (
            <section className="match-controls leaderboard-card">
              <div className="public-match-control-header">
                <div>
                  <div className="match-stat-label">Display launchers</div>
                  <h2>Search and filter</h2>
                </div>
                <div className="public-control-summary" aria-label="Display launcher results summary">
                  <strong>{filteredDisplayCount}</strong>
                  <span>shown</span>
                </div>
              </div>

              <form className="match-controls-row" onSubmit={handleSearchSubmit}>
                <input
                  className="match-search"
                  type="text"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Display, location, or ruleset"
                />
                <button className="match-search-button" type="submit">Search</button>
                {hasActiveFilters && (
                  <button className="match-search-button secondary" type="button" onClick={handleClearFilters}>
                    Clear
                  </button>
                )}
              </form>

              <div className="match-filter-grid" aria-label="Display launcher filters">
                <label className="match-filter-field">
                  <span>Type</span>
                  <select className="match-sort" value={typeFilter} onChange={(event) => handleTypeFilterChange(event.target.value)}>
                    {DISPLAY_TYPE_FILTERS.map((value) => (
                      <option key={value} value={value}>
                        {value === "all" ? "All displays" : value === "matches" ? "Match displays" : "Tournament displays"}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="match-filter-field">
                  <span>Status</span>
                  <select className="match-sort" value={statusFilter} onChange={(event) => handleStatusFilterChange(event.target.value)}>
                    {DISPLAY_STATUS_FILTERS.map((value) => (
                      <option key={value} value={value}>
                        {value === "all" ? "All statuses" : formatStatus(value)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>
          )}

          {loadErrors.length > 0 && (
            <div className="leaderboard-card leaderboard-message-card">
              <span>Showing available display launchers. {loadErrors.join(" ")}</span>
            </div>
          )}

          {displayCount === 0 && (
            <section className="leaderboard-card director-start-panel list-empty-panel">
              <div>
                <div className="owner-registration-section-label">No displays</div>
                <h2>No display launchers yet</h2>
                <div className="empty-filter-summary" aria-label="Display launcher state">
                  <span>ARC matches: 0</span>
                  <span>Tournaments: 0</span>
                </div>
              </div>
              <div className="director-action-row">
                <Link to="/matches/manage/new" className="match-search-button leaderboard-inline-link">
                  Create Match
                </Link>
                <Link to="/matches/manage/tournaments" className="registration-auth-secondary leaderboard-inline-link">
                  Tournament Admin
                </Link>
              </div>
            </section>
          )}

          {displayCount > 0 && filteredDisplayCount === 0 && (
            <section className="leaderboard-card director-start-panel list-empty-panel">
              <div>
                <div className="owner-registration-section-label">No results</div>
                <h2>No display launchers match these filters</h2>
                <div className="empty-filter-summary" aria-label="Active display filters">
                  {activeFilterLabels.map((label) => (
                    <span key={label}>{label}</span>
                  ))}
                </div>
              </div>
              <div className="director-action-row">
                <button className="registration-auth-secondary leaderboard-inline-link detail-link-button" type="button" onClick={handleClearFilters}>
                  Reset
                </button>
              </div>
            </section>
          )}

          {filteredMatchDisplays.length > 0 && (
            <section className="leaderboard-card tournament-display-section">
              <div className="tournament-section-header">
                <div>
                  <div className="owner-registration-section-label">Match displays</div>
                  <h2>Match leaderboards</h2>
                </div>
              </div>
              <div className="director-match-row-list">
                {filteredMatchDisplays.map((match) => (
                  <article key={match.id} className="director-match-row leaderboard-link-card">
                    <Link
                      to={match.displayPath} state={{ displayReturn: location.pathname + location.search }}
                      className="card-click-target"
                      aria-label={`Open fullscreen leaderboard for ${match.title}`}
                    />
                    <div>
                      <div className="leaderboard-pill-row">
                        <span className="open-match-chip">{match.familyLabel}</span>
                        <span className={`status-pill status-${match.status}`}>{formatStatus(match.status)}</span>
                        <span className="open-match-chip">{match.visibility === "published_public" ? "Public" : "Private"}</span>
                      </div>
                      <strong>{match.title}</strong>
                      <span>{match.dateLabel} · {match.locationLabel}</span>
                    </div>
                    <div className="director-row-actions">
                      <Link to={match.displayPath} state={{ displayReturn: location.pathname + location.search }} className="match-search-button leaderboard-inline-link">Fullscreen</Link>
                      <Link to={displayAllRowsPath(match.displayPath)} state={{ displayReturn: location.pathname + location.search }} className="registration-auth-secondary leaderboard-inline-link">All scores</Link>
                      <Link to={match.workbenchPath} className="detail-link">Workbench</Link>
                      {match.visibility === "published_public" ? (
                        <Link to={match.publicPath} className="detail-link">Public Page</Link>
                      ) : (
                        <span className="director-disabled-action">Private page</span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {filteredTournamentDisplays.length > 0 && (
            <section className="leaderboard-card tournament-display-section">
              <div className="tournament-section-header">
                <div>
                  <div className="owner-registration-section-label">Tournament displays</div>
                  <h2>Standings leaderboards</h2>
                </div>
              </div>
              <div className="director-match-row-list">
                {filteredTournamentDisplays.map((tournament) => (
                  <article key={`${tournament.sourceLabel}:${tournament.id}`} className="director-match-row leaderboard-link-card">
                    <Link
                      to={tournament.displayPath} state={{ displayReturn: location.pathname + location.search }}
                      className="card-click-target"
                      aria-label={`Open tournament leaderboard for ${tournament.title}`}
                    />
                    <div>
                      <div className="leaderboard-pill-row">
                        <span className="open-match-chip">{tournament.sourceLabel}</span>
                        <span className={`status-pill status-${tournament.status}`}>{formatStatus(tournament.status)}</span>
                        <span className="open-match-chip">{tournament.familyLabel}</span>
                      </div>
                      <strong>{tournament.title}</strong>
                      <span>{tournament.dateLabel}</span>
                    </div>
                    <div className="director-row-actions">
                      <Link to={tournament.displayPath} state={{ displayReturn: location.pathname + location.search }} className="match-search-button leaderboard-inline-link">Fullscreen</Link>
                      <Link to={displayAllRowsPath(tournament.displayPath)} state={{ displayReturn: location.pathname + location.search }} className="registration-auth-secondary leaderboard-inline-link">All scores</Link>
                      <Link to={tournament.workbenchPath} className="detail-link">Workbench</Link>
                      {tournament.visibility === "public" ? (
                        <Link to={tournament.publicPath} className="detail-link">Public Page</Link>
                      ) : (
                        <span className="director-disabled-action">Private page</span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </section>
  );
}

export const DirectorDisplays_Page = {
  path: "/matches/manage/displays",
  element: <DirectorDisplaysPage />,
};
